import { FlexBoxColumn } from 'components/UI/Layout/FlexBox';
import SpinnerModal from 'components/UI/Spinner/SpinnerModal';
import DefaultText from 'components/UI/Typography/DefaultText';
import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FontSize, FontWeight } from 'modules/styles/variables';
import {
  PreRegistrationCheckListStepByIdSelector,
  PreRegistrationFetchingSelector,
  PreRegistrationPatientInfoSelector,
  PreRegistrationVisitDetailSelector
} from 'store/preRegistration/selectors';
import {
  currentLocationPathNameSelector,
  previousLocationPathNameSelector
} from 'store/router/selectors';
import { RootState } from 'store/types';

import { CheckListItemPageProps } from './types';
import { PreRegAdditionalInfoText, errors } from './constant';
import { MuiBox } from 'theme/material-ui';
import {
  PreRegDatePicker,
  PreRegHeader,
  PreRegInfoLabel,
  PreRegSelectProvider,
  PreRegTimePicker,
  PreRegTextArea,
  PreRegConfirmModal,
  PreRegSelect
} from './common';
import StyledButton from 'components/UI/Button/StyledButton';
import { useMutation } from '@apollo/react-hooks';
import { handleError, headerWithJWT } from './utils';
import { UPDATE_PRE_REGISTRATION_CHECKLIST } from 'store/preRegistration/actions';
import { UPDATE_CHECKLIST_GUARANTOR } from 'services/orchestration/mutations';
import analyticsService, { AmplitudeEventData, AnalyticsEvent } from 'services/AnalyticsService';
import { Formik } from 'formik';
import Spacer from 'components/UI/Layout/Spacer';
import { ProviderSummary } from 'store/findProvider/types';
import RequiredFieldsLegend, { StyledAsterisk } from 'components/RequiredFieldsLegend';
import { PersonalInfoInputLabel } from './PersonalInformation/styled';
import YesNoRadioGroup from 'components/PreRegister/YesNoRadioGroup';
import * as Yup from 'yup';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { LOCATION_STATES } from 'modules/constants/LocationStates';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DayJSUtils from '@date-io/dayjs';

interface AdditionalInfoPageProps extends CheckListItemPageProps {
  isFetchingProviders: boolean;
  providers: ProviderSummary[];
}
export function AdditionalInfoComponent(props: AdditionalInfoPageProps) {
  const {
    step,
    isLoading,
    visitDetail,
    patientInfo,
    currentUrl,
    referringUrl,
    providers,
    isFetchingProviders
  } = props;
  const [updateChecklist, { loading }] = useMutation(UPDATE_CHECKLIST_GUARANTOR); // TODO Change Mutation
  const history = useHistory();
  const dispatch = useDispatch();

  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!step) {
      history.replace('/pre-reg/checklist');
    }
  }, []);

  const onConfirmButtonPress = async values => {
    const fin = visitDetail.finNumber;
    const mrn = patientInfo?.id;
    const mutationPayload = {
      arType: visitDetail.arType,
      visitType: visitDetail.type,
      checkListStepId: step.id,
      facilityCode: visitDetail.facilityCode,
      fin,
      mrn,
      stepData: {
        status: 'COMPLETE'
      }
    };
    const res = await updateChecklist({
      variables: {
        payload: mutationPayload
      },
      context: {
        clientName: 'orchestration',
        headers: headerWithJWT()
      }
    });

    if (res.data.updateChecklistGuarantorInfoStep.success) {
      dispatch({
        type: UPDATE_PRE_REGISTRATION_CHECKLIST,
        payload: res.data.updateChecklistGuarantorInfoStep.data
      });
      const amplitudeEventData: AmplitudeEventData = {
        currentUrl,
        referringUrl,
        /* eslint-disable-next-line @typescript-eslint/camelcase */
        form_location: visitDetail?.location?.name || '',
        /* eslint-disable-next-line @typescript-eslint/camelcase */
        form_detail_type: `Hospital Registration: ${step.title}`,
        /* eslint-disable-next-line @typescript-eslint/camelcase */
        form_detail_additional_info: ``
      };
      analyticsService.logEvent(AnalyticsEvent.HospitalFormDetailComplete, amplitudeEventData);
      history.push('/pre-reg/checklist');
    } else {
      handleError(errors[step.type], history);
    }
  };

  const initialValues = {
    accidentDate: null,
    accidentLastWorked: null,
    accidentHow: '',
    accidentState: 'UT',
    accidentTime: null,
    accidentType: '',
    accidentWhere: '',
    covidSymptoms: PreRegAdditionalInfoText.chooseOne,
    isAccident: '',
    lastSeen: null,
    pcp: 'Dr. Feel Good',
    referring: 'Dr. Dolittle',
    travel: '',
    travelLocations: ''
  };

  const yupIsAccidentString = Yup.string().when('isAccident', {
    is: 'yes',
    then: Yup.string().required()
  });
  const yupIsAccidentSelect = Yup.string()
    .when('isAccident', {
      is: 'yes',
      then: Yup.string().required()
    })
    .notOneOf([PreRegAdditionalInfoText.chooseOne]);

  const yupIsAccidentDate = Yup.date()
    .nullable(true)
    .when('isAccident', {
      is: 'yes',
      then: Yup.date().required()
    });

  const validationSchema = Yup.object().shape({
    accidentDate: yupIsAccidentDate,
    accidentLastWorked: Yup.date()
      .nullable(true)
      .when('accidentType', {
        is: 'Employment Related',
        then: Yup.date().required()
      }),
    accidentHow: yupIsAccidentString,
    accidentState: yupIsAccidentSelect,
    accidentTime: yupIsAccidentDate,
    accidentType: yupIsAccidentSelect,
    accidentWhere: yupIsAccidentString,
    covidSymptoms: Yup.string()
      .required()
      .notOneOf([PreRegAdditionalInfoText.chooseOne]),
    isAccident: Yup.string().required(),
    lastSeen: Yup.date()
      .nullable(true)
      .when('isAccident', { is: 'no', then: Yup.date().required() }),
    pcp: Yup.string().required(),
    referring: Yup.string().required(),
    travel: Yup.string().required(),
    travelLocations: Yup.string().when('travel', {
      is: 'yes',
      then: Yup.string()
        .required()
        .notOneOf([PreRegAdditionalInfoText.chooseOne])
    })
  });

  return (
    <FlexBoxColumn data-testid="pre-registeration-additional-info" minHeight="100%">
      <SpinnerModal isLoading={!!(isLoading || loading)} />
      <PreRegConfirmModal
        title={PreRegAdditionalInfoText.accidentSummary}
        confirmLabel="OK"
        isOpen={showModal}
        icon="InfoIcon"
        subTitleElement={() => (
          <span>
            <p>{PreRegAdditionalInfoText.accidentSummaryInfo1}</p>
            <p>{PreRegAdditionalInfoText.accidentSummaryInfo2}</p>
          </span>
        )}
        onConfirm={() => setShowModal(false)}
      />
      <PreRegHeader title={PreRegAdditionalInfoText.title} />
      <MuiBox m={1} p={1}>
        <DefaultText fontSize={FontSize.large} fontWeight={FontWeight.bold}>
          {PreRegAdditionalInfoText.description}
        </DefaultText>
        <RequiredFieldsLegend />
      </MuiBox>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={onConfirmButtonPress}
        validationSchema={validationSchema}
      >
        {({
          handleSubmit,
          handleChange,
          setFieldValue,
          setFieldTouched,
          values,
          isValid,
          touched
        }) => {
          return (
            <form>
              <MuiPickersUtilsProvider utils={DayJSUtils}>
                <MuiBox p={2}>
                  <PreRegInfoLabel
                    label={PreRegAdditionalInfoText.attendingPhysician}
                    value="Dr. Igor PHD"
                  />
                  <Spacer spacing="medium" />
                  <PreRegSelectProvider
                    defaultValue={values.pcp}
                    name="pcp"
                    label={PreRegAdditionalInfoText.pcp}
                    providers={
                      providers.length
                        ? [
                            ...providers.slice(0, 5),
                            'My PCP is not on this list',
                            "I don't have a PCP",
                            'I prefer not to designate a PCP'
                          ]
                        : []
                    }
                    isFetching={isFetchingProviders}
                    setFieldValue={(field: string, val: any) => {
                      setFieldValue(field, val);
                      setFieldTouched(field, true, true);
                    }}
                    required
                  />
                  <Spacer spacing="medium" />

                  <PreRegSelectProvider
                    defaultValue={values.referring}
                    name="referring"
                    label={PreRegAdditionalInfoText.referringPhysician}
                    providers={
                      providers.length
                        ? [
                            ...providers.slice(0, 5),
                            'My Referring Physician is not on this list',
                            'I referred myself'
                          ]
                        : []
                    }
                    isFetching={isFetchingProviders}
                    setFieldValue={(field: string, val: any) => {
                      setFieldValue(field, val);
                      setFieldTouched(field, true, true);
                    }}
                    required
                  />
                  <Spacer spacing="large" />

                  <div>
                    <YesNoRadioGroup
                      formLabel={PreRegAdditionalInfoText.travel}
                      name="travel"
                      onBlur={() => setFieldTouched('travel', true, true)}
                      onChange={handleChange('travel')}
                      required
                      value={values.travel}
                    />
                    {values.travel === 'yes' ? (
                      <PreRegSelect
                        data-testid="travelLocations-select"
                        label={PreRegAdditionalInfoText.whichLocation}
                        name="travelLocations"
                        onBlur={() => setFieldTouched('travelLocations', true, true)}
                        onChange={handleChange('travelLocations')}
                        required
                        value={values.travelLocations}
                      >
                        {[
                          PreRegAdditionalInfoText.chooseOne,
                          ...PreRegAdditionalInfoText.travelLocations
                        ].map(loc => (
                          <option key={`loc_${loc}`} value={loc}>
                            {loc}
                          </option>
                        ))}
                      </PreRegSelect>
                    ) : null}
                    <PreRegSelect
                      data-testid="covidSymptoms-select"
                      label={PreRegAdditionalInfoText.covid}
                      name="covidSymptoms"
                      onBlur={() => setFieldTouched('covidSymptoms', true, true)}
                      onChange={handleChange('covidSymptoms')}
                      required
                      value={values.covidSymptoms}
                    >
                      {[
                        PreRegAdditionalInfoText.chooseOne,
                        ...PreRegAdditionalInfoText.covidSymptoms
                      ].map(symptom => (
                        <option key={`symptom_${symptom}`} value={symptom}>
                          {symptom}
                        </option>
                      ))}
                    </PreRegSelect>
                  </div>

                  <div className="isAccident">
                    <YesNoRadioGroup
                      formLabel={PreRegAdditionalInfoText.isAccident}
                      name="isAccident"
                      onBlur={() => setFieldTouched('isAccident', true, true)}
                      onChange={handleChange('isAccident')}
                      required
                      value={values.isAccident}
                    />
                    {values.isAccident === 'yes' ? (
                      <>
                        <PreRegDatePicker
                          label={PreRegAdditionalInfoText.accidentDate}
                          name="accidentDate"
                          onBlur={() => setFieldTouched('accidentDate', true, true)}
                          onChange={(date: MaterialUiPickersDate) =>
                            setFieldValue('accidentDate', date)
                          }
                          required
                          value={values.accidentDate}
                        />

                        <PersonalInfoInputLabel>
                          {PreRegAdditionalInfoText.accidentTime}
                          <StyledAsterisk />
                        </PersonalInfoInputLabel>
                        <PreRegTimePicker
                          name="accidentTime"
                          onBlur={() => setFieldTouched('accidentTime', true, true)}
                          onChange={(time: MaterialUiPickersDate) =>
                            setFieldValue('accidentTime', time)
                          }
                          value={values.accidentTime}
                        />
                        <Spacer spacing="large" />

                        <PreRegTextArea
                          handleRequestOpen={() => setShowModal(true)}
                          maxLength={150}
                          multiline
                          name="accidentHow"
                          onBlur={() => setFieldTouched('accidentHow', true, true)}
                          onChange={handleChange('accidentHow')}
                          rowsMax={4}
                          title={PreRegAdditionalInfoText.accidentHow}
                          value={values.accidentHow}
                        />

                        <PreRegTextArea
                          maxLength={50}
                          name="accidentWhere"
                          onBlur={() => setFieldTouched('accidentWhere', true, true)}
                          onChange={handleChange('accidentWhere')}
                          title={PreRegAdditionalInfoText.accidentWhere}
                          value={values.accidentWhere}
                        />

                        <PreRegSelect
                          label={PreRegAdditionalInfoText.accidentType}
                          name="accidentType"
                          onBlur={() => setFieldTouched('accidentType', true, true)}
                          onChange={handleChange('accidentType')}
                          required
                          value={values.accidentType}
                        >
                          {[
                            PreRegAdditionalInfoText.chooseOne,
                            ...PreRegAdditionalInfoText.accidentTypes
                          ].map(type => (
                            <option key={`type_${type}`} value={type}>
                              {type}
                            </option>
                          ))}
                        </PreRegSelect>

                        {values.accidentType === 'Employment Related' ? (
                          <PreRegDatePicker
                            label={PreRegAdditionalInfoText.accidentLastWorked}
                            name="accidentLastWorked"
                            onBlur={() => setFieldTouched('accidentLastWorked', true, true)}
                            onChange={(date: MaterialUiPickersDate) => {
                              setFieldValue('accidentLastWorked', date);
                            }}
                            required
                            value={values.accidentLastWorked}
                          />
                        ) : null}

                        <PreRegSelect
                          label={PreRegAdditionalInfoText.accidentState}
                          name="accidentState"
                          onBlur={() => setFieldTouched('accidentState', true, true)}
                          onChange={handleChange('accidentState')}
                          required
                          value={values.accidentState}
                        >
                          {[
                            ...LOCATION_STATES,
                            { value: 'NOT_IN_USA', label: 'Outside the United States' }
                          ]
                            .sort(a => (a.value === 'UT' ? -1 : 1))
                            .map(state => (
                              <option key={`type_${state.value}`} value={state.value}>
                                {state.label}
                              </option>
                            ))}
                        </PreRegSelect>
                      </>
                    ) : (
                      <PreRegDatePicker
                        label={PreRegAdditionalInfoText.lastSeen}
                        name="lastSeen"
                        onChange={(date: MaterialUiPickersDate) => setFieldValue('lastSeen', date)}
                        onBlur={() => setFieldTouched('lastSeen', true, true)}
                        required
                        value={values.lastSeen}
                      />
                    )}
                  </div>

                  <StyledButton
                    disabled={!isValid || !Object.keys(touched).length}
                    data-testid="submit-button"
                    onPress={handleSubmit}
                    title="confirm"
                  >
                    Confirm
                  </StyledButton>
                </MuiBox>
              </MuiPickersUtilsProvider>
            </form>
          );
        }}
      </Formik>
    </FlexBoxColumn>
  );
}

const mapStateToProps = (state: RootState, props: CheckListItemPageProps) => {
  const id = props.location.state?.id || '';
  return {
    // providers: searchDataSelector(state), TODO: When we know the API
    providers: [
      { displayName: 'Dr. Feel Good' },
      { displayName: 'Dr. Strange Love' },
      { displayName: 'Dr. Who' },
      { displayName: 'Dr. Frankenstein' },
      { displayName: 'Dr. Dolittle' }
    ],
    // isFetchingProviders: searchFetchingSelector(state), TODO: When we know the API
    step: PreRegistrationCheckListStepByIdSelector(id)(state),
    visitDetail: PreRegistrationVisitDetailSelector(state),
    patientInfo: PreRegistrationPatientInfoSelector(state),
    isLoading: PreRegistrationFetchingSelector(state),
    currentUrl: currentLocationPathNameSelector(state),
    referringUrl: previousLocationPathNameSelector(state)
  };
};

export default connect(mapStateToProps)(AdditionalInfoComponent);
